import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { leaveQueryKeys } from '@/hooks/leave/queryKeys';
import { payrollKeys } from './queryKeys';

/**
 * Consolidated real-time subscription for payroll_runs + payslips.
 * 
 * Uses a SINGLE channel with two postgres_changes handlers to reduce
 * total WebSocket channel count. Both tables share the same company_id filter
 * and trigger the same cache invalidations.
 */
export const usePayrollRealtimeSubscription = () => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!profile?.company_id) return;

    // Cleanup existing channel (HMR safety)
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const companyId = profile.company_id;
    const channelName = `payroll-realtime-${companyId}`;

    // Debounced: a 50-employee run emits one payslip event per employee, which
    // would otherwise trigger 50 refetch storms mid-run.
    const invalidatePayrollCaches = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const client = queryClientRef.current;
        // Payroll history + run details + payslips (previously missing).
        client.invalidateQueries({ queryKey: payrollKeys.runs() });
        client.invalidateQueries({ queryKey: ['payslips'] });
        client.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === 'timesheets' && key[1] === 'company';
          },
        });
        client.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      }, 1000);
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_runs',
          filter: `company_id=eq.${companyId}`,
        },
        () => invalidatePayrollCaches()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payslips',
          filter: `company_id=eq.${companyId}`,
        },
        () => invalidatePayrollCaches()
      )
      .subscribe((status) => {
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          console.warn('[Payroll Realtime] Connection closed/error');
        }
      });

    channelRef.current = channel;

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [profile?.company_id]);
};

// Backward-compatible exports (used by PayrollRealtimeProvider)
export const usePayrollRunsRealtimeSubscription = usePayrollRealtimeSubscription;
/** @deprecated Consolidated into usePayrollRealtimeSubscription */
export const usePayslipsRealtimeSubscription = () => {
  // No-op: payslips are now handled by the consolidated hook
};
